/**
 * strumentiService — la console legge e governa gli strumenti a canone (atlante cap. 251, tappa 4).
 *
 * Legge: il listino (`strumenti_a_canone`) e i contatori dei consumi di ogni azienda (`tenants/{t}/consumi`, che il
 * super utente legge per regola). Il periodo NON si ricalcola qui: ogni contatore porta l'inizio e la fine che il
 * server ha deciso, così la console dice lo stesso mese della fattura.
 * Scrive: solo attraverso le funzioni del server (`consoleModificaStrumento`), perché un cambio di prezzo crea prezzi su
 * Stripe e il listino non lo scrive il browser.
 */

import { collection, doc, getDoc, getDocs, limit, orderBy, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import {
  COLLEZIONE_STRUMENTI_A_CANONE, strumentoDalDocumento, type StrumentoACanone,
} from '@shared/strumenti/listino';
import {
  contatoreDaDati, righeDiConsumo, strumentiAccesi, type ContatoreDelPeriodo, type RigaDiConsumo,
} from '@shared/strumenti/consumoDellAzienda';
import { tettoDelloStrumento, type TettoDelloStrumento } from '@shared/strumenti/tetto';
import { listTenants, type TenantConsoleListItem } from './tenantService';
import { millisDa } from '@shared/strumenti/attivazione';
import type { RichiestaModificaStrumento } from '@shared/strumenti/modificaListino';
import { COLLEZIONE_COSTI_VERI, costiVeriDaDati, type CostiVeriDelloStrumento } from '@shared/strumenti/margine';

export async function leggiListino(): Promise<Map<string, StrumentoACanone>> {
  const snap = await getDocs(collection(db, COLLEZIONE_STRUMENTI_A_CANONE));
  const listino = new Map<string, StrumentoACanone>();
  for (const d of snap.docs) {
    const esito = strumentoDalDocumento(d.id, d.data());
    if (esito.ok) listino.set(d.id, esito.strumento);
    else console.error('[strumenti] voce del listino scartata', d.id, esito.motivo);
  }
  return listino;
}

/** Un contatore con il suo periodo, come l'ha scritto il server. */
export interface ContatoreConPeriodo extends ContatoreDelPeriodo {
  readonly chiave: string;
  readonly inizio_ms: number;
  readonly fine_ms: number;
}

/** Gli ultimi contatori di un'azienda, dal più recente. */
export async function leggiContatori(tenantId: string, quanti = 6): Promise<ContatoreConPeriodo[]> {
  const snap = await getDocs(query(collection(db, 'tenants', tenantId, 'consumi'), orderBy('inizio', 'desc'), limit(quanti)));
  return snap.docs.flatMap((d) => {
    const dati = d.data();
    const contatore = contatoreDaDati(dati);
    const inizio = millisDa(dati.inizio);
    const fine = millisDa(dati.fine);
    if (!contatore || inizio === null || fine === null) return [];
    return [{ ...contatore, chiave: d.id, inizio_ms: inizio, fine_ms: fine }];
  });
}

/** Il contatore del periodo in corso, se nel periodo c'è stato almeno un consumo. */
export function contatoreInCorso(contatori: readonly ContatoreConPeriodo[], oraMs: number): ContatoreConPeriodo | null {
  return contatori.find((c) => c.inizio_ms <= oraMs && oraMs < c.fine_ms) ?? null;
}

export interface EsitoModificaStrumento {
  strumento: string;
  ambiente: 'test' | 'live';
  prima: Pick<StrumentoACanone, 'canone_mensile_eur' | 'consumo' | 'in_vendita' | 'al_tetto'>;
  dopo: Pick<StrumentoACanone, 'canone_mensile_eur' | 'consumo' | 'in_vendita' | 'al_tetto'>;
  prezzi_creati: string[];
  altro_ambiente_da_riallineare: boolean;
}

export async function modificaStrumento(r: RichiestaModificaStrumento): Promise<EsitoModificaStrumento> {
  const chiama = httpsCallable<RichiestaModificaStrumento, EsitoModificaStrumento>(functions, 'consoleModificaStrumento');
  return (await chiama(r)).data;
}

/** Una riga della pagina «Consumi»: un'azienda, uno strumento acceso, il suo consumo del periodo in corso. */
export interface RigaConsumoAzienda {
  readonly tenant: TenantConsoleListItem;
  readonly riga: RigaDiConsumo;
  readonly tetto: TettoDelloStrumento;
  /** Il periodo del contatore in corso (null se nel periodo non si è ancora consumato niente). */
  readonly periodo: { inizio_ms: number; fine_ms: number } | null;
  /** Quanto ci è costato lo strumento nello stesso periodo (cap. 452); null = niente registrato (o non misurato). */
  readonly costi: CostiVeriDelloStrumento | null;
}

/**
 * Il cambio dollaro → euro del registro dei costi (`platform_costi/fornitori`, lo stesso con cui il server converte i
 * costi dei modelli). Null se il registro non c'è: allora il margine non si calcola, e la pagina lo dice.
 */
async function leggiCambio(): Promise<number | null> {
  const dati = (await getDoc(doc(db, 'platform_costi', 'fornitori'))).data();
  const cambio = (dati?.analisi as { eur_per_usd?: unknown } | undefined)?.eur_per_usd;
  return typeof cambio === 'number' && Number.isFinite(cambio) && cambio > 0 ? cambio : null;
}

/** I costi veri di un'azienda in un periodo (`platform_costi_veri`, solo per chi gestisce la piattaforma). */
async function leggiCostiVeri(tenantId: string, chiavePeriodo: string): Promise<Record<string, unknown> | undefined> {
  return (await getDoc(doc(db, COLLEZIONE_COSTI_VERI, `${tenantId}__${chiavePeriodo}`))).data();
}

/**
 * Tutte le aziende con almeno uno strumento acceso e il consumo del periodo in corso. Un contatore per azienda, letti
 * insieme: le aziende sono decine, non migliaia.
 */
export async function leggiConsumiDiTutte(oraMs: number): Promise<{ listino: Map<string, StrumentoACanone>; righe: RigaConsumoAzienda[]; eurPerUsd: number | null }> {
  const [listino, aziende, eurPerUsd] = await Promise.all([leggiListino(), listTenants(), leggiCambio()]);
  const conStrumenti = aziende.filter((a) => strumentiAccesi(a, listino).length > 0);
  const contatori = await Promise.all(conStrumenti.map((a) => leggiContatori(a.id, 2)));
  const inCorso = conStrumenti.map((_, i) => contatoreInCorso(contatori[i], oraMs));
  // I costi veri dello STESSO periodo del contatore: incasso e costo si confrontano riga per riga.
  const costi = await Promise.all(conStrumenti.map((a, i) => (inCorso[i] ? leggiCostiVeri(a.id, inCorso[i]!.chiave) : Promise.resolve(undefined))));
  const righe = conStrumenti.flatMap((tenant, i) => {
    const contatore = inCorso[i];
    return righeDiConsumo(tenant, listino, contatore, oraMs).map((riga) => ({
      tenant, riga, tetto: tettoDelloStrumento(tenant, riga.strumento),
      periodo: contatore ? { inizio_ms: contatore.inizio_ms, fine_ms: contatore.fine_ms } : null,
      costi: costiVeriDaDati(costi[i], riga.strumento.capability),
    }));
  });
  return { listino, righe, eurPerUsd };
}

export interface CondizioniSuMisura {
  canone_mensile_eur: number;
  quota_compresa: number;
  prezzo_extra_eur: number;
}

export interface EsitoSuMisura {
  prima: CondizioniSuMisura & { su_misura: boolean };
  dopo: CondizioniSuMisura & { su_misura: boolean };
  prezzi_creati: string[];
  /** `applicato` = l'abbonamento ha già i prezzi nuovi; `al_rinnovo` = c'è extra nel mese, si cambia dopo il rinnovo. */
  passaggio: 'nessun_abbonamento' | 'applicato' | 'al_rinnovo';
}

/** Dà (o, con `condizioni: null`, toglie) le condizioni su misura di un'azienda per uno strumento. */
export async function impostaCondizioniSuMisura(r: { tenant_id: string; strumento: string; condizioni: CondizioniSuMisura | null; nota?: string }): Promise<EsitoSuMisura> {
  const chiama = httpsCallable<typeof r, EsitoSuMisura>(functions, 'consoleCondizioniSuMisura');
  return (await chiama(r)).data;
}

/** Regala (o, con `regalo: null`, toglie) uno strumento a un'azienda, gratis fino a una data o senza scadenza. */
export async function regalaStrumento(r: { tenant_id: string; strumento: string; regalo: { fino_al_ms: number | null; motivo: string } | null }): Promise<void> {
  await httpsCallable<typeof r, unknown>(functions, 'consoleRegalaStrumento')(r);
}
